import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import '../../styles/design-system/index.css';

const LibraryLandingPage = () => {
  const navigate = useNavigate();

  const categories = [
    { icon: 'chart', title: 'AI PPT', desc: '수업용 프레젠테이션', count: '과목별·학년별', link: '/ai-ppt' },
    { icon: 'note', title: 'AI 학습지', desc: 'OX·단답형·서술형 학습지', count: '시험·수행평가용', link: '/ai-worksheet' },
    { icon: 'palette', title: 'AI 이미지', desc: '색칠공부·포스터·플래시카드', count: '7가지 용도', link: '/ai-image' },
    { icon: 'clipboard', title: '생기부 예시', desc: '세특·행발·활동 예시', count: '과목별 모음', link: '/create-saenggibu' },
  ];

  const faqData = [
    { q: '라이브러리의 자료는 무료인가요?', a: '네, 라이브러리의 모든 교사 공유 자료는 무료로 열람하고 활용할 수 있습니다. 로그인하지 않아도 볼 수 있고, 받으려면 로그인하면 됩니다.' },
    { q: '내가 만든 자료는 어디서 볼 수 있나요?', a: 'AI로 만든 PPT, 학습지, 이미지는 라이브러리 "내 자료" 탭에 자동 저장됩니다. 언제든 다시 보고 다운로드할 수 있어요. 다른 선생님과 나누고 싶으면 "나눔터에 공유" 버튼으로 간편하게 공유할 수 있습니다.' },
    { q: '자료의 품질은 어떤가요?', a: '모든 자료는 왓퀴즈 AI가 생성한 것으로, 교과과정에 맞춰 제작됩니다. 교사가 직접 검수하고 공유한 자료이므로 수업에 바로 활용할 수 있는 수준입니다.' },
    { q: '인디스쿨이나 참쌤스쿨과 뭐가 다른가요?', a: '인디스쿨·참쌤스쿨은 교사가 직접 만든 자료를 공유합니다. 왓퀴즈 라이브러리는 AI가 만든 자료를 원클릭으로 공유하므로, 디자인 스킬 없이도 고품질 자료를 나눌 수 있습니다. PPT, 학습지, 이미지가 통합 제공됩니다.' },
    { q: 'TpT(Teachers Pay Teachers)처럼 자료를 판매할 수 있나요?', a: '현재는 무료 공유만 지원합니다. 향후 인기 자료 제작자에게 PRO 이용권 등 리워드를 지급할 계획입니다.' },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "왓퀴즈 교사 자료 라이브러리",
        "description": "교사들이 AI로 만든 PPT, 학습지, 이미지를 공유하고 활용하는 자료 라이브러리.",
        "url": "https://whatquiz.co.kr/library"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>교사 자료 라이브러리 | AI 수업자료 공유 · PPT · 학습지 · 이미지 | 왓퀴즈</title>
        <meta name="description" content="교사들이 AI로 만든 수업 PPT, 학습지, 이미지를 무료로 공유하고 활용하는 자료 라이브러리. 과목별·학년별 검색, 원클릭 공유, 내 수업에 바로 활용. 왓퀴즈 교사 자료실." />
        <meta name="keywords" content="교사 자료실, 수업자료 공유, 교사 수업자료 사이트, 무료 학습지 다운로드, 교사 PPT 자료, 수업 PPT 공유, 학습지 공유, 교육 자료 공유, 교사 자료 나눔, 수업자료 사이트" />
        <link rel="canonical" href="https://whatquiz.co.kr/library" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/library" />
        <meta property="og:title" content="교사 자료 라이브러리 | AI 수업자료 공유 | 왓퀴즈" />
        <meta property="og:description" content="교사들이 AI로 만든 PPT, 학습지, 이미지를 무료로 공유. 과목별·학년별 검색." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div style={{ fontFamily: 'var(--whatquiz-font-family)', backgroundColor: '#fff', minHeight: '100vh' }}>
        {/* 히어로 */}
        <section style={{ padding: '100px 20px 80px', textAlign: 'center', background: 'linear-gradient(180deg, #e8f3ff 0%, #fff 100%)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
              교사 전용 자료실
            </span>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: '800', color: '#111', lineHeight: '1.3', marginBottom: '16px' }}>
              교사가 만들고,<br />교사가 나누는 자료 라이브러리
            </h1>
            <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7', marginBottom: '32px' }}>
              교사 자료 라이브러리란, 교사들이 AI로 만든 수업 PPT, 학습지, 이미지 등의 교육 자료를 공유하고 활용하는 플랫폼입니다.
              디자인 스킬 없이도 AI가 만든 고품질 자료를 원클릭으로 공유하고, 다른 교사의 자료를 내 수업에 바로 활용하세요.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/chat')} style={{ minHeight: '48px', padding: '14px 32px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
                AI로 자료 만들기
              </button>
              <button onClick={() => navigate('/community')} style={{ padding: '16px 32px', background: '#fff', color: '#334155', fontSize: '16px', fontWeight: '600', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' }}>
                커뮤니티 둘러보기
              </button>
            </div>
          </div>
        </section>

        {/* 자료 카테고리 */}
        <section style={{ maxWidth: '960px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '40px' }}>AI가 만든 수업자료, 바로 활용하세요</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {categories.map((cat, idx) => (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                aria-label={`${cat.title} 자료 보기`}
                onClick={() => navigate(cat.link)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(cat.link);
                  }
                }}
                style={{ background: '#f8f9fa', border: 'none', borderRadius: '16px', padding: '28px 20px', textAlign: 'center', cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e8f3ff'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}><NamedGrayIcon name={cat.icon} /></div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '4px' }}>{cat.title}</h3>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>{cat.desc}</p>
                <span style={{ fontSize: '12px', color: '#3182f6', fontWeight: '600' }}>{cat.count}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 공유 프로세스 */}
        <section style={{ padding: '60px 20px', background: '#fff' }}>
          <div style={{ maxWidth: '720px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '40px' }}>원클릭으로 공유하세요</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              {[
                { step: '1', title: 'AI로 자료 만들기', desc: 'PPT, 학습지, 이미지를 AI로 생성합니다' },
                { step: '2', title: '자동 저장', desc: '만든 자료는 라이브러리 "내 자료"에 자동 저장' },
                { step: '3', title: '나눔터에 공유', desc: '다른 선생님들과 나누고 싶으면 버튼 한 번으로 공유' },
              ].map((item, idx) => (
                <div key={idx} style={{ background: '#f8f9fa', borderRadius: '16px', padding: '24px', textAlign: 'center', border: 'none' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#3182f6', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', marginBottom: '12px' }}>{item.step}</div>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111', marginBottom: '6px' }}>{item.title}</h3>
                  <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 경쟁사 비교 */}
        <section style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '32px' }}>교사 자료 공유 플랫폼 비교</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700' }}>비교 항목</th>
                  <th style={{ padding: '12px', fontWeight: '700' }}>인디스쿨</th>
                  <th style={{ padding: '12px', fontWeight: '700' }}>미리캔버스</th>
                  <th style={{ padding: '12px', fontWeight: '700' }}>TpT</th>
                  <th style={{ padding: '12px', fontWeight: '700', color: '#3182f6' }}>왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['자료 생성', '직접 제작', '템플릿 편집', '직접 제작', 'AI 자동 생성'],
                  ['공유 방식', '파일 업로드', '링크 공유', '판매/무료', '원클릭 공유'],
                  ['자료 유형', 'HWP, PPT', '디자인 템플릿', '다양', 'PPT+학습지+이미지'],
                  ['교사 업무 연계', '없음', '없음', '없음', '생기부+채점 올인원'],
                  ['대상', '초등', '전체', '글로벌', '초·중·고 전체'],
                ].map(([cat, a, b, c, d], idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{cat}</td>
                    <td style={{ padding: '10px 12px' }}>{a}</td>
                    <td style={{ padding: '10px 12px' }}>{b}</td>
                    <td style={{ padding: '10px 12px' }}>{c}</td>
                    <td style={{ padding: '10px 12px', fontWeight: '600', color: '#3182f6' }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '32px' }}>자주 묻는 질문</h2>
          {faqData.map((faq, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{faq.q}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{faq.a}</p>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section style={{ padding: '60px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #e8f3ff 0%, #fff 100%)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111', marginBottom: '12px' }}>AI로 만든 수업자료,<br />다른 선생님과 나눠보세요</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>가입만 하면 무료로 시작할 수 있습니다</p>
          <button onClick={() => navigate('/chat')} style={{ minHeight: '48px', padding: '14px 32px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
            AI로 자료 만들기 시작
          </button>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default LibraryLandingPage;
